import { HttpHeaders, HttpClient, HttpParams, HttpRequest, HttpEventType } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfig } from '@shared/utils/app-config';
import { Observable, of, map, tap } from 'rxjs';

@Injectable()
export class BaseApiService {
  public httpHeaders: HttpHeaders = new HttpHeaders({
    'Content-Type': 'application/json',
    Accept: 'application/json',
  });

  public constructor(private http: HttpClient, private appConfig: AppConfig) {}

  public get<T>(url: string, params: HttpParams = new HttpParams()): Observable<T> {
    return this.http.get<T>(`${this.appConfig.baseApiUrl}${url}`, {
      headers: this.httpHeaders,
      params,
    });
  }

  public getNewApi<T>(url: string, params: HttpParams = new HttpParams()): Observable<T> {
    return this.http.get<T>(`${this.appConfig.baseNewApiUrl}${url}`, {
      headers: this.httpHeaders,
      params,
    });
  }

  public getWithOriginalUrl<T>(url: string, responseType: string, parameters: HttpParams = new HttpParams()): Observable<T> {
    const options = {
      headers: this.httpHeaders,
      params: parameters,
      origin: window.location.host,
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (options as any)['responseType'] = responseType;

    return this.http.get<T>(`${url}`, options);
  }

  public getWithHeaderAppend<T>(url: string, name: string, value: string, params: HttpParams = new HttpParams()): Observable<T> {
    return this.http.get<T>(`${this.appConfig.baseApiUrl}${url}`, {
      headers: this.httpHeaders.append(name, value),
      params,
    });
  }

  public getWithHeaderAppendNewApi<T>(url: string, name: string, value: string, params: HttpParams = new HttpParams()): Observable<T> {
    return this.http.get<T>(`${this.appConfig.baseNewApiUrl}${url}`, {
      headers: this.httpHeaders.append(name, value),
      params,
    });
  }

  public patchWithHeaderAppend<T>(url: string, name: string, value: string, params: HttpParams = new HttpParams(), data: object = {}): Observable<T> {
    return this.http.patch<T>(`${this.appConfig.baseApiUrl}${url}`, JSON.stringify(data), {
      headers: this.httpHeaders.append(name, value),
      params,
    });
  }

  public patchWithHeaderAppendNewApi<T>(url: string, name: string, value: string, params: HttpParams = new HttpParams(), data: object = {}): Observable<T> {
    return this.http.patch<T>(`${this.appConfig.baseNewApiUrl}${url}`, JSON.stringify(data), {
      headers: this.httpHeaders.append(name, value),
      params,
    });
  }

  public post<T>(url: string, data: object = {}): Observable<T> {
    return this.http.post<T>(`${this.appConfig.baseApiUrl}${url}`, JSON.stringify(data), { headers: this.httpHeaders });
  }

  public postNewApi<T>(url: string, data: object = {}): Observable<T> {
    return this.http.post<T>(`${this.appConfig.baseNewApiUrl}${url}`, JSON.stringify(data), { headers: this.httpHeaders });
  }

  public postFormData<T>(url: string, data: FormData): Observable<T> {
    return this.http.post<T>(`${this.appConfig.baseApiUrl}${url}`, data);
  }

  public postFormDataNewApi<T>(url: string, data: FormData): Observable<T> {
    return this.http.post<T>(`${this.appConfig.baseNewApiUrl}${url}`, data);
  }

  public postWithHeaderAppend<T>(url: string, data: object = {}, name: string, value: string, params: HttpParams = new HttpParams()): Observable<T> {
    return this.http.post<T>(`${this.appConfig.baseApiUrl}${url}`, JSON.stringify(data), { headers: this.httpHeaders.append(name, value), params });
  }

  public postWithHeaderAppendNewApi<T>(url: string, data: object = {}, name: string, value: string, params: HttpParams = new HttpParams()): Observable<T> {
    return this.http.post<T>(`${this.appConfig.baseNewApiUrl}${url}`, JSON.stringify(data), { headers: this.httpHeaders.append(name, value), params });
  }

  public postFormDataEvents<T>(url: string, data: FormData): Observable<T | number> {
    return this.http.post<T>(`${this.appConfig.baseApiUrl}${url}`, data, { reportProgress: true, observe: 'events' }).pipe(
      map((event) => {
        if (event.type === HttpEventType.UploadProgress) {
          return Math.round((100 * event.loaded) / (event?.total ?? 1));
        }
        if (event.type === HttpEventType.Response && event.body) {
          return event.body;
        }

        return 0;
      })
    );
  }

  public postFormDataEventsNewApi<T>(url: string, data: FormData): Observable<T | number> {
    return this.http.post<T>(`${this.appConfig.baseNewApiUrl}${url}`, data, { reportProgress: true, observe: 'events' }).pipe(
      map((event) => {
        if (event.type === HttpEventType.UploadProgress) {
          return Math.round((100 * event.loaded) / (event?.total ?? 1));
        }
        if (event.type === HttpEventType.Response && event.body) {
          return event.body;
        }

        return 0;
      })
    );
  }

  public put<T>(url: string, data: object = {}): Observable<T> {
    return this.http.put<T>(`${this.appConfig.baseApiUrl}${url}`, JSON.stringify(data), { headers: this.httpHeaders });
  }

  public putNewApi<T>(url: string, data: object = {}): Observable<T> {
    return this.http.put<T>(`${this.appConfig.baseNewApiUrl}${url}`, JSON.stringify(data), { headers: this.httpHeaders });
  }

  public delete<T>(url: string): Observable<T> {
    return this.http.delete<T>(`${this.appConfig.baseApiUrl}${url}`, {
      headers: this.httpHeaders,
    });
  }

  public deleteNewApi<T>(url: string): Observable<T> {
    return this.http.delete<T>(`${this.appConfig.baseNewApiUrl}${url}`, {
      headers: this.httpHeaders,
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public deleteWithBody<R>(url: string, data: object = {}): Observable<R> {
    return this.http.request<R>('delete', `${this.appConfig.baseApiUrl}${url}`, { body: JSON.stringify(data), headers: this.httpHeaders });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public deleteWithBodyNewApi<R>(url: string, data: object = {}): Observable<R> {
    return this.http.request<R>('delete', `${this.appConfig.baseNewApiUrl}${url}`, { body: JSON.stringify(data), headers: this.httpHeaders });
  }

  public patch<T>(url: string, data: object = {}): Observable<T> {
    return this.http.patch<T>(`${this.appConfig.baseApiUrl}${url}`, JSON.stringify(data), { headers: this.httpHeaders });
  }

  public patchNewApi<T>(url: string, data: object = {}): Observable<T> {
    return this.http.patch<T>(`${this.appConfig.baseNewApiUrl}${url}`, JSON.stringify(data), { headers: this.httpHeaders });
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  public request(request: HttpRequest<any>): Observable<any> {
    return this.http.request(request);
  }

  public download(path: string): Observable<unknown> {
    return this.post(path).pipe(
      tap((next) => {
        const link = document.createElement('a');
        link.setAttribute('href', `${next}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
    );
  }

  public downloadExternalURL(url: string): Observable<unknown> {
    const link = document.createElement('a');
    link.setAttribute('href', `${url}`);
    document.body.appendChild(link);
    link.click();
    link.remove();

    return of('');
  }

  public openPdf(url: string): void {
    const headers = new HttpHeaders({
      'Content-Type': 'application/pdf',
    });

    const options = {
      headers: headers,
      responseType: 'blob' as 'json',
      observe: 'response' as const,
    };

    this.http.get<BlobPart>(`${this.appConfig.baseApiUrl}${url}`, options).subscribe((response) => {
      if (response.body) {
        const blob = new Blob([response.body], { type: 'application/pdf' });
        window.open(URL.createObjectURL(blob), '_blank');
      }
    });
  }
}
